// Shared student name sorting: lets any page sort student lists alphabetically
// by first or last name, with the choice persisted across the app.
import { useEffect, useState } from 'react';

export const STUDENT_NAME_ORDER_KEY = 'ae_student_name_order';
export const STUDENT_NAME_ORDER_EVENT = 'ae:student-name-order-change';

export function getStudentNameOrder() {
  try {
    const stored = window.localStorage.getItem(STUDENT_NAME_ORDER_KEY);
    return stored === 'first' ? 'first' : 'last';
  } catch (_e: any) {
    return 'last';
  }
}

export function setStudentNameOrder(order) {
  const normalized = order === 'first' ? 'first' : 'last';
  try {
    window.localStorage.setItem(STUDENT_NAME_ORDER_KEY, normalized);
  } catch (_e: any) {
    // ignore storage failures (e.g. private browsing)
  }
  window.dispatchEvent(new CustomEvent(STUDENT_NAME_ORDER_EVENT, { detail: normalized }));
}

// React hook so any component can read/react to the current name-sort preference.
export function useStudentNameOrder() {
  const [order, setOrder] = useState(getStudentNameOrder());

  useEffect(() => {
    const onChange = (e) => setOrder(e.detail === 'first' ? 'first' : 'last');
    window.addEventListener(STUDENT_NAME_ORDER_EVENT, onChange);
    window.addEventListener('storage', () => setOrder(getStudentNameOrder()));
    return () => {
      window.removeEventListener(STUDENT_NAME_ORDER_EVENT, onChange);
    };
  }, []);

  return [order, setStudentNameOrder];
}

function nameParts(student) {
  const first = (student?.first_name || '').trim();
  const last = (student?.last_name || '').trim();
  return { first, last };
}

// Builds the sort key used for alphabetizing, based on the chosen order.
export function getStudentSortKey(student, order = 'last') {
  const { first, last } = nameParts(student);
  const fallback = student?.full_name || student?.label || `Student ${student?.student_id ?? ''}`;
  if (order === 'first') {
    return (first ? `${first} ${last}` : fallback).toLowerCase();
  }
  return (last ? `${last} ${first}` : fallback).toLowerCase();
}

// Sorts a copy of the given students array alphabetically by first or last name.
export function sortStudentsByName(students, order = 'last') {
  return [...(students || [])].sort((a, b) =>
    getStudentSortKey(a, order).localeCompare(getStudentSortKey(b, order))
  );
}
